// src/components/DocumentForgery/UploadHistory/UploadHistory.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentCard from './DocumentCard';
import { getDocumentListHandler } from '../../../services/documentForgery';
import { showErrorToast } from '../../../utility/helper';

const GRID_GAP   = 12;
const CARD_MIN_W = 140;

// Status filter options — matches backend DocumentStatus choices
const STATUS_FILTERS = [
  { key: '',                 label: 'All Documents' },
  { key: 'Marked for Review', label: '🔖 Under Review' },
  // { key: 'accepted',          label: '✓ Accepted' },
  // { key: 'rejected',          label: '✗ Rejected' },
  // { key: 'pending',           label: 'Pending' },
];

const UploadHistory = () => {
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFilter,  setStatusFilter]  = useState('');
  const [data,          setData]          = useState([]);
  const [filteredData,  setFilteredData]  = useState([]);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [itemsPerPage,  setItemsPerPage]  = useState(30);
  const [totalCount,    setTotalCount]    = useState(0);
  const [loading,       setLoading]       = useState(true);

  const containerRef = useRef(null);
  const gridRef      = useRef(null);
  const navigate     = useNavigate();

  // Responsive items-per-page
  const recalc = useCallback(() => {
    const container = containerRef.current;
    const grid      = gridRef.current;
    if (!container || !grid) return;
    const firstCard = grid.querySelector('.file-card');
    const cardH     = firstCard ? firstCard.offsetHeight + GRID_GAP : 200 + GRID_GAP;
    const cardW     = firstCard ? firstCard.offsetWidth  + GRID_GAP : CARD_MIN_W + GRID_GAP;
    const containerW = container.clientWidth;
    const containerH = window.innerHeight - container.getBoundingClientRect().top - 120;
    const cols = Math.max(1, Math.floor((containerW + GRID_GAP) / cardW));
    const rows = Math.max(1, Math.floor((containerH + GRID_GAP) / cardH));
    setItemsPerPage(cols * rows);
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(() => recalc());
    return () => cancelAnimationFrame(raf);
  }, [filteredData, recalc]);

  useEffect(() => {
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [recalc]);

  // Fetch all documents (client-side filter for search + status)
  useEffect(() => {
    setLoading(true);
    getDocumentListHandler({ ordering: '-created_at', page_size: 200 })
      .then(res => {
        const items = res?.data?.results || res?.data || [];
        const arr   = Array.isArray(items) ? items : [];
        setData(arr);
      })
      .catch(() => showErrorToast('Failed to load documents'))
      .finally(() => setLoading(false));
  }, []);

  // Apply both search and status filters together
  useEffect(() => {
    if (!Array.isArray(data)) return;
    const q = searchTerm.toLowerCase();
    const filtered = data.filter(d => {
      const name = (d?.name || d?.document_name || '').toLowerCase();
      const matchSearch = q === '' || name.includes(q);
      // status field from API is snake_case: "marked_for_review", "pending", etc.
      // status_display is human-readable: "Marked for Review" — don't use that for filtering
      const docStatus   = d?.status_display || 'pending';
      const matchStatus = statusFilter === '' || docStatus === statusFilter;
      return matchSearch && matchStatus;
    });
    setFilteredData(filtered);
    setTotalCount(filtered.length);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, data]);

  // Counts for badge display
  const reviewCount = data.filter(d => d?.status_display === 'Marked for Review').length;

  // Pagination
  const totalPages  = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const start       = (currentPage - 1) * itemsPerPage;
  const currentData = filteredData.slice(start, start + itemsPerPage);

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    for (let i = Math.max(1, currentPage - delta); i <= Math.min(totalPages, currentPage + delta); i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="upload-history" ref={containerRef}>

      {/* ── Top bar ── */}
      <div className="upload-history__topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>All Documents</span>
          {!loading && (
            <span style={{
              background: '#e0f2fe', color: '#0284c7',
              borderRadius: 20, fontSize: 11, fontWeight: 700,
              padding: '2px 8px',
            }}>
              {filteredData.length}
            </span>
          )}
        </div>
        <div className="upload-history__search">
          <span className="search-icon" style={{ fontSize: 13 }}>🔍</span>
          <input
            type="text"
            placeholder="Search documents"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* ── Status filter tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', paddingBottom: 2 }}>
        {STATUS_FILTERS.map(f => {
          const isActive = statusFilter === f.key;
          const isReview = f.key === 'Marked for Review';
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              style={{
                padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                fontWeight: isActive ? 700 : 600,
                fontSize: 12,
                border: `1px solid ${isActive
                  ? (isReview ? '#16a34a' : '#44ace4')
                  : '#e5e7eb'}`,
                background: isActive
                  ? (isReview ? '#16a34a' : '#44ace4')
                  : '#fff',
                color: isActive ? '#fff' : '#6b7280',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              {f.label}
              {/* Show count badge on "Under Review" tab */}
              {isReview && reviewCount > 0 && (
                <span style={{
                  background: isActive ? 'rgba(255,255,255,0.3)' : '#16a34a',
                  color: '#fff',
                  borderRadius: 99, fontSize: 10, fontWeight: 800,
                  padding: '0px 6px', lineHeight: 1.6,
                  minWidth: 18, textAlign: 'center',
                }}>
                  {reviewCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Grid ── */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: '#9ca3af', fontSize: 14 }}>
          Loading documents…
        </div>
      ) : (
        <div
          className="docs-grid"
          ref={gridRef}
          style={{
            gridAutoRows: '240px',   // fixed row height — cards are same height
          }}
        >
          {currentData.length > 0
            ? currentData.map(doc => <DocumentCard data={doc} key={doc?.id || doc?.document_id} />)
            : (
              <div className="docs-empty" style={{ gridColumn: '1/-1', textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                {statusFilter
                  ? `No ${STATUS_FILTERS.find(f => f.key === statusFilter)?.label || ''} documents found.`
                  : 'No documents found.'}
              </div>
            )
          }
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>← Prev</button>
          {currentPage > 3 && <><button onClick={() => setCurrentPage(1)}>1</button><span style={{ color: '#9ca3af', fontSize: 12 }}>…</span></>}
          {pageNumbers().map(n => (
            <button key={n} className={currentPage === n ? 'active' : ''} onClick={() => setCurrentPage(n)}>{n}</button>
          ))}
          {currentPage < totalPages - 2 && <><span style={{ color: '#9ca3af', fontSize: 12 }}>…</span><button onClick={() => setCurrentPage(totalPages)}>{totalPages}</button></>}
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
};

export default UploadHistory;